import React, { useEffect, useState, useRef } from "react";
import { db } from "./firebase";
import {
  collection,
  addDoc,
  onSnapshot,
  deleteDoc,
  updateDoc,
  doc,
  setDoc,
  getDoc,
} from "firebase/firestore";

const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href =
  "https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap";
document.head.appendChild(fontLink);

const SCRAPE_FUNCTION_URL =
  "https://us-central1-frc-inventory-b7bb1.cloudfunctions.net/scrapeProduct";

const SCAN_FUNCTION_URL =
  "https://us-central1-frc-inventory-b7bb1.cloudfunctions.net/scanOrder";

const SEASONS = ["ALL", "2025", "2026", "2027", "2028", "2029"];

const VENDORS = [
  "Thrifty Bot", "AndyMark First Choice", "Harbor Freight",
  "Swerve Drive Specialties", "SWYFT Robotics", "FoamNoodles",
  "Carpet Spectrum", "Limelight Vision", "McMaster-Carr",
  "Home Depot", "Lowe's", "Amazon", "AndyMark", "REV Robotics",
  "WestCoast Products", "CTR Electronics", "Michaels", "Last Anvil",
];

const STATUS_COLORS = {
  RECEIVED: { bg: "#d1fae5", text: "#065f46", dot: "#10b981" },
  SHIPPED:  { bg: "#ede9fe", text: "#5b21b6", dot: "#8b5cf6" },
  ORDERED:  { bg: "#dbeafe", text: "#1e40af", dot: "#3b82f6" },
  BACKORDER:{ bg: "#fef3c7", text: "#92400e", dot: "#f59e0b" },
};

const BLANK_FORM = {
  description: "", partNumber: "", vendor: "McMaster-Carr",
  date: "", qty: "", unitCost: "", status: "ORDERED",
  season: new Date().getFullYear().toString(),
};

export default function App() {
  const [items, setItems]               = useState([]);
  const [orders, setOrders]             = useState({});
  const [search, setSearch]             = useState("");
  const [editingId, setEditingId]       = useState(null);
  const [season, setSeason]             = useState(new Date().getFullYear().toString());
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [activeTab, setActiveTab]       = useState("inventory");
  const [form, setForm]                 = useState(BLANK_FORM);

  // URL scraper
  const [scrapeUrl, setScrapeUrl]         = useState("");
  const [scraping, setScraping]           = useState(false);
  const [scrapeError, setScrapeError]     = useState("");
  const [scrapeSuccess, setScrapeSuccess] = useState(false);

  // Email/screenshot scanner
  const [scanMode, setScanMode]           = useState("url");
  const [emailText, setEmailText]         = useState("");
  const [scanning, setScanning]           = useState(false);
  const [scanError, setScanError]         = useState("");
  const [scanSuccess, setScanSuccess]     = useState(false);
  const [scanResults, setScanResults]     = useState([]);
  const [imagePreview, setImagePreview]   = useState(null);
  const [imageBase64, setImageBase64]     = useState(null);
  const imageInputRef                     = useRef(null);
  const formRef                           = useRef(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "inventory"), (snap) => {
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "orders"), (snap) => {
      const data = {};
      snap.docs.forEach((d) => { data[d.id] = d.data(); });
      setOrders(data);
    });
    return () => unsub();
  }, []);

  const num   = (v) => parseFloat((v || "").toString().replace(/[^0-9.]/g, "")) || 0;
  const clean = (v) => (v || "").toString().trim().toLowerCase();

  // ── URL Auto-fill ────────────────────────────────────────────────────────────
  const handleScrape = async () => {
    if (!scrapeUrl.trim()) return;
    setScraping(true); setScrapeError(""); setScrapeSuccess(false);
    try {
      const res  = await fetch(`${SCRAPE_FUNCTION_URL}?url=${encodeURIComponent(scrapeUrl)}`);
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || "Failed to fetch product");
      setForm((f) => ({
        ...f,
        description: data.description || f.description,
        partNumber:  data.partNumber  || f.partNumber,
        unitCost:    data.unitCost    || f.unitCost,
        vendor:      data.vendor      || f.vendor,
      }));
      setScrapeSuccess(true); setScrapeUrl("");
      setTimeout(() => setScrapeSuccess(false), 4000);
    } catch (err) {
      setScrapeError(err.message || "Could not load product info. Fill in manually.");
    } finally { setScraping(false); }
  };

  // ── Email Scanner (via Cloud Function) ───────────────────────────────────────
  const handleEmailScan = async () => {
    if (!emailText.trim()) return;
    setScanning(true); setScanError(""); setScanSuccess(false); setScanResults([]);
    try {
      const res = await fetch(SCAN_FUNCTION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailText }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || "Failed to scan email");
      const parsed = data.items;
      if (!Array.isArray(parsed) || parsed.length === 0) throw new Error("No items found in this email.");
      setScanResults(parsed);
      setScanSuccess(true);
    } catch (err) {
      setScanError(err.message || "Could not parse email. Try copying more of the email text.");
    } finally { setScanning(false); }
  };

  // ── Image Scanner (via Cloud Function) ───────────────────────────────────────
  const handleImageScan = async () => {
    if (!imageBase64) return;
    setScanning(true); setScanError(""); setScanSuccess(false); setScanResults([]);
    try {
      const res = await fetch(SCAN_FUNCTION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageBase64, imageType: "image/jpeg" }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || "Failed to scan image");
      const parsed = data.items;
      if (!Array.isArray(parsed) || parsed.length === 0) throw new Error("No items found in this image.");
      setScanResults(parsed);
      setScanSuccess(true);
    } catch (err) {
      setScanError(err.message || "Could not read image. Try a clearer screenshot.");
    } finally { setScanning(false); }
  };

  // ── Image Upload ─────────────────────────────────────────────────────────────
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result.split(",")[1];
      setImageBase64(base64);
      setImagePreview(ev.target.result);
      setScanResults([]); setScanError(""); setScanSuccess(false);
    };
    reader.readAsDataURL(file);
  };

  // ── Apply one scan result to form ────────────────────────────────────────────
  const applyScanResult = (item) => {
    setForm((f) => ({
      ...f,
      description: item.description || f.description,
      partNumber:  item.partNumber  || f.partNumber,
      vendor:      item.vendor      || f.vendor,
      unitCost:    item.unitCost    || f.unitCost,
      qty:         item.qty         || f.qty,
      date:        item.date        || f.date,
    }));
    setScanResults([]);
    setScanSuccess(false);
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // ── Add ALL scan results to inventory ────────────────────────────────────────
  const addAllScanResults = async (results) => {
    for (const item of results) {
      const qty  = num(item.qty);
      const unit = num(item.unitCost);
      await addDoc(collection(db, "inventory"), {
        description: item.description || "Unnamed Item",
        partNumber:  item.partNumber  || "N/A",
        vendor:      item.vendor      || "Unknown",
        date:        item.date        || "No Date",
        qty, unitCost: unit, itemTotal: qty * unit,
        status: "ORDERED",
        season: form.season || new Date().getFullYear().toString(),
      });
    }
    setScanResults([]);
    setScanSuccess(false);
    setEmailText("");
    setImagePreview(null);
    setImageBase64(null);
    alert(`✅ Added ${results.length} item${results.length !== 1 ? "s" : ""} to inventory!`);
  };

  // ── Save Item ────────────────────────────────────────────────────────────────
  const saveItem = async () => {
    const qty  = num(form.qty);
    const unit = num(form.unitCost);
    const data = {
      description: form.description || "Unnamed Item",
      partNumber:  form.partNumber  || "N/A",
      vendor: form.vendor, date: form.date || "No Date",
      qty, unitCost: unit, itemTotal: qty * unit,
      status: form.status,
      season: form.season || new Date().getFullYear().toString(),
    };
    if (editingId) {
      await updateDoc(doc(db, "inventory", editingId), data);
      setEditingId(null);
    } else {
      await addDoc(collection(db, "inventory"), data);
    }
    setForm(BLANK_FORM);
  };

  const removeItem = async (id) => await deleteDoc(doc(db, "inventory", id));

  const editItem = (item) => {
    setEditingId(item.id);
    setForm({ ...item });
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const cancelEdit = () => { setEditingId(null); setForm(BLANK_FORM); };

  // ── Filter + Group ───────────────────────────────────────────────────────────
  const filtered = items.filter((i) => {
    const q = search.toLowerCase();
    return (
      ((i.description || "").toLowerCase().includes(q) ||
       (i.partNumber  || "").toLowerCase().includes(q) ||
       (i.vendor      || "").toLowerCase().includes(q) ||
       (i.date        || "").toLowerCase().includes(q)) &&
      (season       === "ALL" || i.season === season) &&
      (statusFilter === "ALL" || i.status === statusFilter)
    );
  });

  const grouped = filtered.reduce((acc, item) => {
    const key = `${clean(item.vendor)}__${clean(item.date)}`;
    if (!acc[key]) acc[key] = { vendor: item.vendor, date: item.date, items: [] };
    acc[key].items.push(item);
    return acc;
  }, {});

  // ── Totals ───────────────────────────────────────────────────────────────────
  const seasonItems    = season === "ALL" ? items : items.filter((i) => i.season === season);
  const allItemsTotal  = seasonItems.reduce((s, i) => s + (i.itemTotal || 0), 0);
  const receivedTotal  = seasonItems.filter((i) => i.status === "RECEIVED").reduce((s, i) => s + (i.itemTotal || 0), 0);
  const shippedTotal   = seasonItems.filter((i) => i.status === "SHIPPED").reduce((s, i) => s + (i.itemTotal || 0), 0);
  const orderedTotal   = seasonItems.filter((i) => i.status === "ORDERED").reduce((s, i) => s + (i.itemTotal || 0), 0);
  const backorderTotal = seasonItems.filter((i) => i.status === "BACKORDER").reduce((s, i) => s + (i.itemTotal || 0), 0);
  const outstandingTotal = allItemsTotal - receivedTotal;

  const vendorTotals = seasonItems.reduce((acc, item) => {
    const v = item.vendor || "Unknown";
    acc[v] = (acc[v] || 0) + (item.itemTotal || 0);
    return acc;
  }, {});

  const allOrders     = Object.values(orders);
  const totalShipping = allOrders.reduce((s, o) => s + num(o.shipping), 0);
  const totalTax      = allOrders.reduce((s, o) => s + num(o.tax), 0);
  const totalDiscount = allOrders.reduce((s, o) => s + num(o.discount), 0);
  const grandTotal    = allItemsTotal + totalShipping + totalTax - totalDiscount;

  const outstandingItems = seasonItems.filter((i) => i.status !== "RECEIVED");

  const seasonBreakdown = SEASONS.filter((s) => s !== "ALL").map((s) => {
    const sItems = items.filter((i) => i.season === s);
    const total       = sItems.reduce((sum, i) => sum + (i.itemTotal || 0), 0);
    const received    = sItems.filter((i) => i.status === "RECEIVED").reduce((sum, i) => sum + (i.itemTotal || 0), 0);
    const outstanding = total - received;
    return { season: s, total, received, outstanding, count: sItems.length };
  }).filter((s) => s.count > 0);

  const getOrder = (key) =>
    orders[key] || { shipping: "", tax: "", discount: "", poNumber: "", confirmationNumber: "", trackingUrl: "" };

  const updateOrder = async (key, field, value) => {
    const ref  = doc(db, "orders", key);
    const snap = await getDoc(ref);
    const existing = snap.exists() ? snap.data() : {};
    await setDoc(ref, { ...existing, [field]: value });
  };

  const exportData = () => {
    const blob = new Blob([JSON.stringify({ inventory: items, orders }, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `partflow-backup-${season}.json`;
    a.click();
  };

  const statusStyle = (s) => STATUS_COLORS[s] || STATUS_COLORS.ORDERED;

  const css = `
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: #0a0f1e; }
    .app { font-family: 'DM Sans', sans-serif; background: #0a0f1e; min-height: 100vh; color: #e2e8f0; padding: 0 0 80px; }

    .header { background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%); border-bottom: 1px solid rgba(99,102,241,0.3); padding: 18px 32px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 100; backdrop-filter: blur(12px); }
    .header-left { display: flex; align-items: center; gap: 14px; }
    .logo-icon { width: 42px; height: 42px; background: linear-gradient(135deg, #6366f1, #a855f7); border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 20px; }
    .header-title-block { display: flex; flex-direction: column; gap: 2px; }
    .app-title { font-family: 'Syne', sans-serif; font-size: 22px; font-weight: 800; color: #f1f5f9; letter-spacing: -0.5px; }
    .app-meta { display: flex; align-items: center; gap: 10px; }
    .app-subtitle { font-size: 12px; color: #64748b; }
    .app-credit { font-size: 11px; color: #4f46e5; font-weight: 600; letter-spacing: 0.3px; background: rgba(99,102,241,0.1); padding: 2px 8px; border-radius: 20px; border: 1px solid rgba(99,102,241,0.2); }

    .season-bar { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
    .season-btn { padding: 6px 14px; border-radius: 20px; border: 1px solid rgba(99,102,241,0.3); background: transparent; color: #94a3b8; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.18s; }
    .season-btn:hover { border-color: #6366f1; color: #c7d2fe; }
    .season-btn.active { background: linear-gradient(135deg, #6366f1, #a855f7); border-color: transparent; color: white; font-weight: 600; }

    .main { max-width: 1280px; margin: 0 auto; padding: 28px 24px; }

    .stats-row { display: grid; grid-template-columns: repeat(5,1fr); gap: 14px; margin-bottom: 28px; }
    .stat-card { background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 60%); border: 1px solid rgba(99,102,241,0.2); border-radius: 14px; padding: 18px; }
    .stat-label { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.8px; font-weight: 600; }
    .stat-value { font-family: 'Syne', sans-serif; font-size: 22px; font-weight: 700; margin-top: 6px; color: #f1f5f9; }
    .stat-accent { color: #818cf8; }
    .stat-green  { color: #10b981; }
    .stat-yellow { color: #f59e0b; }
    .stat-red    { color: #ef4444; }

    .tab-nav { display: flex; gap: 4px; margin-bottom: 24px; background: #0f172a; border-radius: 10px; padding: 4px; width: fit-content; }
    .tab-btn { padding: 8px 20px; border-radius: 8px; border: none; background: transparent; color: #64748b; font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 500; cursor: pointer; transition: all 0.18s; }
    .tab-btn.active { background: #1e293b; color: #e2e8f0; }

    .toolbar { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; align-items: center; }
    .search-wrap { position: relative; flex: 1; min-width: 200px; }
    .search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #475569; font-size: 15px; pointer-events: none; }
    .search-input { width: 100%; padding: 10px 12px 10px 36px; background: #0f172a; border: 1px solid #1e293b; border-radius: 10px; color: #e2e8f0; font-family: 'DM Sans', sans-serif; font-size: 14px; outline: none; transition: border 0.2s; }
    .search-input:focus { border-color: #6366f1; }
    .search-input::placeholder { color: #334155; }
    .filter-select { padding: 9px 14px; background: #0f172a; border: 1px solid #1e293b; border-radius: 10px; color: #94a3b8; font-family: 'DM Sans', sans-serif; font-size: 14px; cursor: pointer; outline: none; }
    .filter-select:focus { border-color: #6366f1; }
    .export-btn { padding: 9px 18px; background: #1e293b; border: 1px solid #334155; border-radius: 10px; color: #94a3b8; font-family: 'DM Sans', sans-serif; font-size: 14px; cursor: pointer; transition: all 0.18s; white-space: nowrap; }
    .export-btn:hover { border-color: #6366f1; color: #c7d2fe; }

    .import-panel { background: linear-gradient(135deg, #0d1229, #170d2e); border: 1px solid rgba(99,102,241,0.4); border-radius: 14px; padding: 18px; margin-bottom: 18px; }
    .import-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
    .import-title { font-size: 12px; color: #a5b4fc; text-transform: uppercase; letter-spacing: 0.8px; font-weight: 700; }
    .import-tabs { display: flex; gap: 4px; background: #0a0f1e; border-radius: 8px; padding: 3px; }
    .import-tab { padding: 5px 14px; border-radius: 6px; border: none; background: transparent; color: #475569; font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 500; cursor: pointer; transition: all 0.15s; }
    .import-tab.active { background: #1e293b; color: #c7d2fe; }
    .scraper-row { display: flex; gap: 10px; align-items: center; }
    .scraper-input { flex: 1; padding: 10px 14px; background: #0a0f1e; border: 1px solid #334155; border-radius: 8px; color: #e2e8f0; font-family: 'DM Sans', sans-serif; font-size: 14px; outline: none; transition: border 0.2s; }
    .scraper-input:focus { border-color: #6366f1; }
    .scraper-input::placeholder { color: #2d3a52; }
    .scraper-btn { padding: 10px 20px; white-space: nowrap; background: linear-gradient(135deg, #6366f1, #a855f7); border: none; border-radius: 8px; color: white; font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; transition: opacity 0.18s; display: flex; align-items: center; gap: 7px; }
    .scraper-btn:hover { opacity: 0.85; }
    .scraper-btn:disabled { opacity: 0.4; cursor: not-allowed; }
    .import-hint { font-size: 12px; color: #3d4f6b; margin-top: 8px; }
    .import-hint b { color: #4e617e; }
    .email-textarea { width: 100%; padding: 12px 14px; background: #0a0f1e; border: 1px solid #334155; border-radius: 8px; color: #e2e8f0; font-family: 'DM Sans', sans-serif; font-size: 13px; outline: none; transition: border 0.2s; resize: vertical; min-height: 120px; }
    .email-textarea:focus { border-color: #6366f1; }
    .email-textarea::placeholder { color: #2d3a52; }
    .image-drop-zone { border: 2px dashed #334155; border-radius: 10px; padding: 24px; text-align: center; cursor: pointer; transition: all 0.18s; background: #0a0f1e; }
    .image-drop-zone:hover { border-color: #6366f1; background: #0d1229; }
    .image-drop-zone img { max-height: 160px; border-radius: 8px; margin-bottom: 10px; }
    .image-drop-text { font-size: 13px; color: #475569; }
    .image-drop-text b { color: #818cf8; }
    .import-error { font-size: 13px; color: #f87171; background: #2d0a0a; padding: 8px 12px; border-radius: 7px; border: 1px solid #7f1d1d; margin-top: 10px; }
    .import-success { font-size: 13px; color: #34d399; background: #022c22; padding: 8px 12px; border-radius: 7px; border: 1px solid #065f46; margin-top: 10px; }
    .scan-results { margin-top: 14px; display: flex; flex-direction: column; gap: 8px; }
    .scan-results-title { font-size: 12px; color: #64748b; text-transform: uppercase; letter-spacing: 0.6px; font-weight: 600; margin-bottom: 4px; }
    .scan-item { background: #0f172a; border: 1px solid #1e293b; border-radius: 10px; padding: 12px 14px; display: flex; align-items: center; gap: 12px; }
    .scan-item-info { flex: 1; }
    .scan-item-name { font-weight: 600; color: #e2e8f0; font-size: 13px; }
    .scan-item-meta { font-size: 12px; color: #475569; margin-top: 2px; }
    .scan-apply-btn { padding: 5px 12px; background: transparent; border: 1px solid #6366f1; border-radius: 6px; color: #818cf8; font-size: 12px; cursor: pointer; white-space: nowrap; transition: all 0.15s; }
    .scan-apply-btn:hover { background: #1e1b4b; color: #c7d2fe; }
    .scan-add-all-btn { width: 100%; margin-top: 8px; padding: 10px; background: linear-gradient(135deg, #059669, #0891b2); border: none; border-radius: 8px; color: white; font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; transition: opacity 0.18s; }
    .scan-add-all-btn:hover { opacity: 0.88; }

    @keyframes spin { to { transform: rotate(360deg); } }
    .spin { display: inline-block; animation: spin 0.7s linear infinite; }

    .form-card { background: #0f172a; border: 1px solid rgba(99,102,241,0.25); border-radius: 16px; padding: 24px; margin-bottom: 28px; }
    .form-title { font-family: 'Syne', sans-serif; font-size: 16px; font-weight: 700; color: #c7d2fe; margin-bottom: 18px; display: flex; align-items: center; gap: 8px; }
    .form-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 12px; margin-bottom: 16px; }
    .form-group { display: flex; flex-direction: column; gap: 5px; }
    .form-label { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.6px; font-weight: 600; }
    .form-input, .form-select { padding: 9px 12px; background: #1e293b; border: 1px solid #334155; border-radius: 8px; color: #e2e8f0; font-family: 'DM Sans', sans-serif; font-size: 14px; outline: none; transition: border 0.2s; }
    .form-input:focus, .form-select:focus { border-color: #6366f1; }
    .form-input::placeholder { color: #475569; }
    .form-select option { background: #1e293b; }
    .form-input.prefilled { border-color: rgba(99,102,241,0.5); background: #1a1a40; }
    .form-actions { display: flex; gap: 10px; }
    .save-btn { padding: 10px 24px; background: linear-gradient(135deg, #6366f1, #a855f7); border: none; border-radius: 8px; color: white; font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; transition: opacity 0.18s; }
    .save-btn:hover { opacity: 0.88; }
    .cancel-btn { padding: 10px 18px; background: #1e293b; border: 1px solid #334155; border-radius: 8px; color: #94a3b8; font-family: 'DM Sans', sans-serif; font-size: 14px; cursor: pointer; }
    .cancel-btn:hover { color: #e2e8f0; }

    .vendor-group { margin-bottom: 24px; }
    .vendor-header { display: flex; align-items: center; gap: 12px; padding: 14px 18px; background: linear-gradient(90deg, #1e1b4b, #0f172a); border-radius: 12px 12px 0 0; border: 1px solid rgba(99,102,241,0.2); border-bottom: none; }
    .vendor-name { font-family: 'Syne', sans-serif; font-size: 15px; font-weight: 700; color: #c7d2fe; }
    .vendor-date { font-size: 12px; color: #64748b; background: #1e293b; padding: 3px 8px; border-radius: 6px; }
    .vendor-subtotal { margin-left: auto; font-size: 13px; color: #818cf8; font-weight: 600; }
    .items-list { border: 1px solid rgba(99,102,241,0.15); border-radius: 0 0 12px 12px; overflow: hidden; background: #0f172a; }
    .item-row { display: grid; grid-template-columns: 1fr auto auto auto auto auto auto; align-items: center; gap: 16px; padding: 14px 18px; border-bottom: 1px solid #1e293b; transition: background 0.15s; }
    .item-row:last-of-type { border-bottom: none; }
    .item-row:hover { background: rgba(30,41,59,0.4); }
    .item-desc { font-weight: 600; color: #e2e8f0; font-size: 14px; }
    .item-pn { font-size: 12px; color: #475569; margin-top: 2px; font-family: monospace; }
    .item-meta { font-size: 13px; color: #64748b; }
    .item-total { font-size: 14px; color: #a5b4fc; font-weight: 600; }
    .status-badge { display: inline-flex; align-items: center; gap: 5px; padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; letter-spacing: 0.4px; text-transform: uppercase; white-space: nowrap; }
    .status-dot { width: 6px; height: 6px; border-radius: 50%; }
    .edit-btn { padding: 6px 12px; background: transparent; border: 1px solid #334155; border-radius: 7px; color: #818cf8; font-size: 13px; cursor: pointer; transition: all 0.15s; white-space: nowrap; }
    .edit-btn:hover { border-color: #6366f1; background: #1e1b4b; }
    .del-btn { padding: 6px 10px; background: transparent; border: 1px solid #334155; border-radius: 7px; color: #ef4444; font-size: 13px; cursor: pointer; transition: all 0.15s; }
    .del-btn:hover { border-color: #ef4444; background: #2d0a0a; }

    .order-footer { background: #0a0f1e; border-top: 1px solid rgba(99,102,241,0.15); padding: 16px 18px; }
    .order-footer-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 14px; align-items: end; }
    .order-footer-field { display: flex; flex-direction: column; gap: 5px; }
    .order-footer-label { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.6px; }
    .order-footer-input { padding: 7px 10px; background: #0f172a; border: 1px solid #1e293b; border-radius: 7px; color: #e2e8f0; font-family: 'DM Sans', sans-serif; font-size: 13px; outline: none; transition: border 0.2s; }
    .order-footer-input:focus { border-color: #6366f1; }
    .order-total-box { text-align: right; }
    .order-total-label { font-size: 12px; color: #64748b; }
    .order-total-value { font-family: 'Syne', sans-serif; font-size: 22px; font-weight: 700; color: #a5b4fc; }

    .tracking-section { background: #0a0f1e; border-top: 1px solid rgba(99,102,241,0.12); padding: 14px 18px; border-radius: 0 0 12px 12px; }
    .tracking-title { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.7px; font-weight: 600; margin-bottom: 10px; }
    .tracking-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 12px; }
    .tracking-field { display: flex; flex-direction: column; gap: 4px; }
    .tracking-label { font-size: 11px; color: #475569; }
    .tracking-input { padding: 7px 10px; background: #0f172a; border: 1px solid #1e293b; border-radius: 7px; color: #e2e8f0; font-family: 'DM Sans', sans-serif; font-size: 13px; outline: none; transition: border 0.2s; }
    .tracking-input:focus { border-color: #6366f1; }
    .tracking-input::placeholder { color: #334155; }
    .tracking-link-row { display: flex; gap: 8px; align-items: center; }
    .tracking-link-btn { padding: 7px 12px; background: #1e293b; border: 1px solid #334155; border-radius: 7px; color: #818cf8; font-size: 12px; font-family: 'DM Sans', sans-serif; cursor: pointer; white-space: nowrap; text-decoration: none; transition: all 0.15s; display: inline-flex; align-items: center; gap: 5px; }
    .tracking-link-btn:hover { border-color: #6366f1; background: #1e1b4b; color: #c7d2fe; }
    .tracking-link-btn.disabled { opacity: 0.35; pointer-events: none; }

    .vendor-summary-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 16px; }
    .vendor-summary-card { background: #0f172a; border: 1px solid #1e293b; border-radius: 12px; padding: 18px; }
    .vscard-vendor { font-size: 13px; font-weight: 600; color: #c7d2fe; margin-bottom: 6px; }
    .vscard-amount { font-family: 'Syne', sans-serif; font-size: 22px; font-weight: 700; color: #e2e8f0; }
    .vscard-pct { font-size: 12px; color: #64748b; margin-top: 2px; }
    .vscard-bar { height: 4px; background: #1e293b; border-radius: 4px; margin-top: 12px; overflow: hidden; }
    .vscard-bar-fill { height: 100%; border-radius: 4px; background: linear-gradient(90deg, #6366f1, #a855f7); }
    .grand-total-card { background: linear-gradient(135deg, #1e1b4b, #0f172a); border: 1px solid rgba(99,102,241,0.35); border-radius: 16px; padding: 28px; margin-top: 24px; display: flex; gap: 40px; align-items: center; flex-wrap: wrap; }
    .gtc-row { display: flex; flex-direction: column; gap: 4px; }
    .gtc-label { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.6px; }
    .gtc-value { font-family: 'Syne', sans-serif; font-size: 20px; font-weight: 700; color: #e2e8f0; }
    .gtc-grand { font-size: 32px; color: #818cf8; }
    .gtc-divider { width: 1px; height: 48px; background: #1e293b; }

    .report-section { margin-bottom: 32px; }
    .report-section-title { font-family: 'Syne', sans-serif; font-size: 17px; font-weight: 700; color: #c7d2fe; margin-bottom: 16px; display: flex; align-items: center; gap: 8px; }
    .status-breakdown-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 14px; margin-bottom: 32px; }
    .sbc { background: #0f172a; border: 1px solid #1e293b; border-radius: 12px; padding: 18px; }
    .sbc-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.7px; font-weight: 600; margin-bottom: 6px; }
    .sbc-amount { font-family: 'Syne', sans-serif; font-size: 24px; font-weight: 700; }
    .sbc-count { font-size: 12px; color: #64748b; margin-top: 4px; }
    .season-table { width: 100%; border-collapse: collapse; }
    .season-table th { text-align: left; font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.6px; padding: 10px 14px; border-bottom: 1px solid #1e293b; }
    .season-table td { padding: 12px 14px; border-bottom: 1px solid #0f172a; font-size: 14px; }
    .season-table tr:last-child td { border-bottom: none; }
    .season-table tr:hover td { background: #0f172a; }
    .season-table-wrap { background: #0a0f1e; border: 1px solid #1e293b; border-radius: 12px; overflow: hidden; }
    .outstanding-list { display: flex; flex-direction: column; gap: 8px; }
    .outstanding-item { background: #0f172a; border: 1px solid #1e293b; border-radius: 10px; padding: 14px 16px; display: flex; align-items: center; gap: 16px; }
    .oi-desc { flex: 1; }
    .oi-name { font-weight: 600; color: #e2e8f0; font-size: 14px; }
    .oi-meta { font-size: 12px; color: #475569; margin-top: 2px; }
    .oi-vendor { font-size: 13px; color: #64748b; }
    .oi-season { font-size: 12px; color: #475569; background: #1e293b; padding: 2px 8px; border-radius: 6px; }
    .oi-total { font-family: 'Syne', sans-serif; font-size: 16px; font-weight: 700; color: #a5b4fc; }
    .empty-report { text-align: center; padding: 40px; color: #334155; background: #0f172a; border: 1px solid #1e293b; border-radius: 12px; }
    .empty-state { text-align: center; padding: 60px; color: #334155; }
    .empty-icon { font-size: 40px; margin-bottom: 12px; }
    .empty-text { font-size: 15px; }

    @media (max-width: 900px) {
      .stats-row { grid-template-columns: repeat(3,1fr); }
      .form-grid { grid-template-columns: repeat(2,1fr); }
      .vendor-summary-grid { grid-template-columns: repeat(2,1fr); }
      .status-breakdown-grid { grid-template-columns: repeat(2,1fr); }
      .item-row { grid-template-columns: 1fr; }
      .header { flex-direction: column; gap: 14px; }
      .tracking-grid { grid-template-columns: 1fr; }
    }
    @media (max-width: 600px) {
      .stats-row { grid-template-columns: repeat(2,1fr); }
      .order-footer-grid { grid-template-columns: repeat(2,1fr); }
    }
  `;

  return (
    <>
      <style>{css}</style>
      <div className="app">

        {/* Header */}
        <header className="header">
          <div className="header-left">
            <div className="logo-icon">⚙️</div>
            <div className="header-title-block">
              <div className="app-title">PartFlow</div>
              <div className="app-meta">
                <span className="app-subtitle">FRC Supply Chain & Procurement</span>
                <span className="app-credit">by Lucas D. Marcato</span>
              </div>
            </div>
          </div>
          <div className="season-bar">
            {SEASONS.map((s) => (
              <button key={s} className={`season-btn${season === s ? " active" : ""}`} onClick={() => setSeason(s)}>
                {s === "ALL" ? "All Seasons" : `${s} Season`}
              </button>
            ))}
          </div>
        </header>

        <div className="main">

          {/* Stats */}
          <div className="stats-row">
            <div className="stat-card"><div className="stat-label">Grand Total</div><div className="stat-value stat-accent">${grandTotal.toFixed(2)}</div></div>
            <div className="stat-card"><div className="stat-label">Received</div><div className="stat-value stat-green">${receivedTotal.toFixed(2)}</div></div>
            <div className="stat-card"><div className="stat-label">Outstanding</div><div className="stat-value stat-yellow">${outstandingTotal.toFixed(2)}</div></div>
            <div className="stat-card"><div className="stat-label">Line Items</div><div className="stat-value">{filtered.length}</div></div>
            <div className="stat-card"><div className="stat-label">Not Received</div><div className="stat-value stat-red">{outstandingItems.length} parts</div></div>
          </div>

          {/* Tabs */}
          <div className="tab-nav">
            <button className={`tab-btn${activeTab === "inventory" ? " active" : ""}`} onClick={() => setActiveTab("inventory")}>📦 Inventory</button>
            <button className={`tab-btn${activeTab === "reports"   ? " active" : ""}`} onClick={() => setActiveTab("reports")}>🚨 Reports</button>
            <button className={`tab-btn${activeTab === "summary"   ? " active" : ""}`} onClick={() => setActiveTab("summary")}>📊 Summary</button>
          </div>

          {/* ══════════ INVENTORY TAB ══════════ */}
          {activeTab === "inventory" && (
            <>
              <div className="form-card" ref={formRef}>
                <div className="form-title">{editingId ? "✏️ Edit Item" : "＋ Add New Item"}</div>

                {!editingId && (
                  <div className="import-panel">
                    <div className="import-header">
                      <span className="import-title">✨ Smart Import</span>
                      <div className="import-tabs">
                        <button className={`import-tab${scanMode === "url"   ? " active" : ""}`} onClick={() => { setScanMode("url");   setScanResults([]); setScanError(""); }}>🔗 URL</button>
                        <button className={`import-tab${scanMode === "email" ? " active" : ""}`} onClick={() => { setScanMode("email"); setScanResults([]); setScanError(""); }}>📧 Email</button>
                        <button className={`import-tab${scanMode === "image" ? " active" : ""}`} onClick={() => { setScanMode("image"); setScanResults([]); setScanError(""); }}>📸 Screenshot</button>
                      </div>
                    </div>

                    {scanMode === "url" && (
                      <>
                        <div className="scraper-row">
                          <input className="scraper-input" placeholder="Paste a McMaster-Carr, REV Robotics, AndyMark, WestCoast Products, or Home Depot URL…" value={scrapeUrl} onChange={(e) => { setScrapeUrl(e.target.value); setScrapeError(""); }} onKeyDown={(e) => e.key === "Enter" && handleScrape()} />
                          <button className="scraper-btn" onClick={handleScrape} disabled={scraping || !scrapeUrl.trim()}>
                            {scraping ? <span className="spin">⟳</span> : "⚡"}{scraping ? "Fetching…" : "Fill Form"}
                          </button>
                        </div>
                        <div className="import-hint">Supports: <b>McMaster-Carr</b> · <b>REV Robotics</b> · <b>AndyMark</b> · <b>WestCoast Products</b> · <b>Home Depot</b></div>
                        {scrapeError   && <div className="import-error">⚠ {scrapeError}</div>}
                        {scrapeSuccess && <div className="import-success">✓ Form filled! Review the fields then hit Add Item.</div>}
                      </>
                    )}

                    {scanMode === "email" && (
                      <>
                        <textarea className="email-textarea" placeholder="Paste your order confirmation email here… PartFlow will extract all items automatically." value={emailText} onChange={(e) => { setEmailText(e.target.value); setScanError(""); setScanResults([]); }} />
                        <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                          <button className="scraper-btn" onClick={handleEmailScan} disabled={scanning || !emailText.trim()} style={{ flex: 1 }}>
                            {scanning ? <span className="spin">⟳</span> : "📧"}{scanning ? "Scanning…" : "Scan Email"}
                          </button>
                        </div>
                        <div className="import-hint">Works with any vendor confirmation email — paste the full email text.</div>
                        {scanError   && <div className="import-error">⚠ {scanError}</div>}
                        {scanSuccess && !scanResults.length && <div className="import-success">✓ Items added to inventory!</div>}
                      </>
                    )}

                    {scanMode === "image" && (
                      <>
                        <div className="image-drop-zone" onClick={() => imageInputRef.current?.click()}>
                          {imagePreview
                            ? <img src={imagePreview} alt="preview" />
                            : <div className="image-drop-text">Click to upload a <b>screenshot</b> of your order email or confirmation page</div>
                          }
                        </div>
                        <input ref={imageInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageUpload} />
                        {imageBase64 && (
                          <button className="scraper-btn" style={{ width: "100%", marginTop: 10, justifyContent: "center" }} onClick={handleImageScan} disabled={scanning}>
                            {scanning ? <span className="spin">⟳</span> : "📸"}{scanning ? "Reading image…" : "Scan Screenshot"}
                          </button>
                        )}
                        <div className="import-hint" style={{ marginTop: 8 }}>Take a screenshot of any order confirmation email and upload it here.</div>
                        {scanError   && <div className="import-error">⚠ {scanError}</div>}
                        {scanSuccess && !scanResults.length && <div className="import-success">✓ Items added to inventory!</div>}
                      </>
                    )}

                    {scanResults.length > 0 && (
                      <div className="scan-results">
                        <div className="scan-results-title">Found {scanResults.length} item{scanResults.length !== 1 ? "s" : ""} — review and import:</div>
                        {scanResults.map((item, idx) => (
                          <div key={idx} className="scan-item">
                            <div className="scan-item-info">
                              <div className="scan-item-name">{item.description}</div>
                              <div className="scan-item-meta">{item.partNumber} · {item.vendor} · Qty {item.qty} · ${item.unitCost}</div>
                            </div>
                            <button className="scan-apply-btn" onClick={() => applyScanResult(item)}>Fill Form →</button>
                          </div>
                        ))}
                        <button className="scan-add-all-btn" onClick={() => addAllScanResults(scanResults)}>
                          ✅ Add All {scanResults.length} Items to Inventory
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="form-grid">
                  <div className="form-group" style={{ gridColumn: "span 2" }}>
                    <label className="form-label">Description</label>
                    <input className={`form-input${form.description ? " prefilled" : ""}`} placeholder="e.g. 1/4-20 Socket Head Cap Screw" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Part Number</label>
                    <input className={`form-input${form.partNumber ? " prefilled" : ""}`} placeholder="e.g. 92196A537" value={form.partNumber} onChange={(e) => setForm({ ...form, partNumber: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Date</label>
                    <input className="form-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Vendor</label>
                    <select className="form-select" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })}>
                      {VENDORS.map((v) => <option key={v}>{v}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Season</label>
                    <select className="form-select" value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })}>
                      {SEASONS.filter((s) => s !== "ALL").map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Qty</label>
                    <input className="form-input" placeholder="0" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Unit Cost ($)</label>
                    <input className={`form-input${form.unitCost ? " prefilled" : ""}`} placeholder="0.00" value={form.unitCost} onChange={(e) => setForm({ ...form, unitCost: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                      <option>ORDERED</option>
                      <option>SHIPPED</option>
                      <option>RECEIVED</option>
                      <option>BACKORDER</option>
                    </select>
                  </div>
                </div>
                <div className="form-actions">
                  <button className="save-btn" onClick={saveItem}>{editingId ? "Update Item" : "Add Item"}</button>
                  {editingId && <button className="cancel-btn" onClick={cancelEdit}>Cancel</button>}
                </div>
              </div>

              <div className="toolbar">
                <div className="search-wrap">
                  <span className="search-icon">🔍</span>
                  <input className="search-input" placeholder="Search description, part number, vendor..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="ALL">All Statuses</option>
                  <option value="ORDERED">Ordered</option>
                  <option value="SHIPPED">Shipped</option>
                  <option value="RECEIVED">Received</option>
                  <option value="BACKORDER">Backorder</option>
                </select>
                <button className="export-btn" onClick={exportData}>⬇ Export JSON</button>
              </div>

              {Object.keys(grouped).length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📭</div>
                  <div className="empty-text">No items found for the current filters.</div>
                </div>
              ) : (
                Object.keys(grouped).map((key) => {
                  const group    = grouped[key];
                  const list     = group.items;
                  const subtotal = list.reduce((a, i) => a + (i.itemTotal || 0), 0);
                  const order    = getOrder(key);
                  const total    = subtotal + num(order.shipping) + num(order.tax) - num(order.discount);
                  return (
                    <div key={key} className="vendor-group">
                      <div className="vendor-header">
                        <span className="vendor-name">{group.vendor}</span>
                        <span className="vendor-date">{group.date}</span>
                        <span className="vendor-subtotal">{list.length} item{list.length !== 1 ? "s" : ""} · ${subtotal.toFixed(2)}</span>
                      </div>
                      <div className="items-list">
                        {list.map((i) => {
                          const sc = statusStyle(i.status);
                          return (
                            <div key={i.id} className="item-row">
                              <div>
                                <div className="item-desc">{i.description}</div>
                                <div className="item-pn">{i.partNumber}</div>
                              </div>
                              <div className="item-meta">Qty {i.qty}</div>
                              <div className="item-meta">${num(i.unitCost).toFixed(2)}/ea</div>
                              <div className="item-total">${(i.itemTotal || 0).toFixed(2)}</div>
                              <span className="status-badge" style={{ background: sc.bg + "22", color: sc.text }}>
                                <span className="status-dot" style={{ background: sc.dot }}></span>
                                {i.status}
                              </span>
                              <button className="edit-btn" onClick={() => editItem(i)}>Edit</button>
                              <button className="del-btn"  onClick={() => removeItem(i.id)}>✕</button>
                            </div>
                          );
                        })}
                        <div className="order-footer">
                          <div className="order-footer-grid">
                            <div className="order-footer-field">
                              <span className="order-footer-label">Shipping ($)</span>
                              <input className="order-footer-input" value={order.shipping} onChange={(e) => updateOrder(key, "shipping", e.target.value)} placeholder="0.00" inputMode="decimal" />
                            </div>
                            <div className="order-footer-field">
                              <span className="order-footer-label">Tax ($)</span>
                              <input className="order-footer-input" value={order.tax} onChange={(e) => updateOrder(key, "tax", e.target.value)} placeholder="0.00" inputMode="decimal" />
                            </div>
                            <div className="order-footer-field">
                              <span className="order-footer-label">Discount ($)</span>
                              <input className="order-footer-input" value={order.discount} onChange={(e) => updateOrder(key, "discount", e.target.value)} placeholder="0.00" inputMode="decimal" />
                            </div>
                            <div className="order-footer-field order-total-box">
                              <span className="order-total-label">Order Total</span>
                              <div className="order-total-value">${total.toFixed(2)}</div>
                            </div>
                          </div>
                        </div>
                        <div className="tracking-section">
                          <div className="tracking-title">📋 PartFlow Tracking</div>
                          <div className="tracking-grid">
                            <div className="tracking-field">
                              <span className="tracking-label">PO Number</span>
                              <input className="tracking-input" placeholder="e.g. PO-2026-001" value={order.poNumber || ""} onChange={(e) => updateOrder(key, "poNumber", e.target.value)} />
                            </div>
                            <div className="tracking-field">
                              <span className="tracking-label">Order / Confirmation #</span>
                              <input className="tracking-input" placeholder="e.g. MC-8473921" value={order.confirmationNumber || ""} onChange={(e) => updateOrder(key, "confirmationNumber", e.target.value)} />
                            </div>
                            <div className="tracking-field">
                              <span className="tracking-label">Tracking URL</span>
                              <div className="tracking-link-row">
                                <input className="tracking-input" style={{ flex: 1 }} placeholder="https://..." value={order.trackingUrl || ""} onChange={(e) => updateOrder(key, "trackingUrl", e.target.value)} />
                                <a href={order.trackingUrl || "#"} target="_blank" rel="noopener noreferrer" className={`tracking-link-btn${!order.trackingUrl ? " disabled" : ""}`} onClick={(e) => { if (!order.trackingUrl) e.preventDefault(); }}>
                                  🔗 Track
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </>
          )}

          {/* ══════════ REPORTS TAB ══════════ */}
          {activeTab === "reports" && (
            <>
              <div className="report-section">
                <div className="report-section-title">💰 Cost by Status — {season === "ALL" ? "All Seasons" : `${season} Season`}</div>
                <div className="status-breakdown-grid">
                  <div className="sbc"><div className="sbc-label" style={{ color: "#10b981" }}>✅ Received</div><div className="sbc-amount" style={{ color: "#10b981" }}>${receivedTotal.toFixed(2)}</div><div className="sbc-count">{seasonItems.filter(i => i.status === "RECEIVED").length} parts</div></div>
                  <div className="sbc"><div className="sbc-label" style={{ color: "#8b5cf6" }}>🚚 Shipped</div><div className="sbc-amount" style={{ color: "#8b5cf6" }}>${shippedTotal.toFixed(2)}</div><div className="sbc-count">{seasonItems.filter(i => i.status === "SHIPPED").length} parts</div></div>
                  <div className="sbc"><div className="sbc-label" style={{ color: "#3b82f6" }}>📋 Ordered</div><div className="sbc-amount" style={{ color: "#3b82f6" }}>${orderedTotal.toFixed(2)}</div><div className="sbc-count">{seasonItems.filter(i => i.status === "ORDERED").length} parts</div></div>
                  <div className="sbc"><div className="sbc-label" style={{ color: "#f59e0b" }}>⏳ Backorder</div><div className="sbc-amount" style={{ color: "#f59e0b" }}>${backorderTotal.toFixed(2)}</div><div className="sbc-count">{seasonItems.filter(i => i.status === "BACKORDER").length} parts</div></div>
                </div>
              </div>

              <div className="report-section">
                <div className="report-section-title">📅 Cost Breakdown by Season</div>
                {seasonBreakdown.length === 0 ? (
                  <div className="empty-report">No season data yet.</div>
                ) : (
                  <div className="season-table-wrap">
                    <table className="season-table">
                      <thead>
                        <tr><th>Season</th><th>Total Parts</th><th>Total Cost</th><th>Received</th><th>Outstanding</th><th>% Received</th></tr>
                      </thead>
                      <tbody>
                        {seasonBreakdown.map((s) => {
                          const pct = s.total > 0 ? (s.received / s.total) * 100 : 0;
                          return (
                            <tr key={s.season}>
                              <td><span style={{ color: "#c7d2fe", fontWeight: 600 }}>{s.season}</span></td>
                              <td style={{ color: "#94a3b8" }}>{s.count}</td>
                              <td style={{ color: "#a5b4fc", fontWeight: 600 }}>${s.total.toFixed(2)}</td>
                              <td style={{ color: "#10b981" }}>${s.received.toFixed(2)}</td>
                              <td style={{ color: s.outstanding > 0 ? "#f59e0b" : "#10b981" }}>${s.outstanding.toFixed(2)}</td>
                              <td>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <div style={{ flex: 1, height: 6, background: "#1e293b", borderRadius: 4, overflow: "hidden" }}>
                                    <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(90deg,#6366f1,#10b981)", borderRadius: 4 }}></div>
                                  </div>
                                  <span style={{ fontSize: 12, color: "#64748b", minWidth: 36 }}>{pct.toFixed(0)}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="report-section">
                <div className="report-section-title">🚨 Outstanding Items — Not Yet Received ({outstandingItems.length})</div>
                {outstandingItems.length === 0 ? (
                  <div className="empty-report">🎉 All items have been received!</div>
                ) : (
                  <div className="outstanding-list">
                    {outstandingItems.sort((a, b) => (b.itemTotal || 0) - (a.itemTotal || 0)).map((i) => {
                      const sc = statusStyle(i.status);
                      return (
                        <div key={i.id} className="outstanding-item">
                          <div className="oi-desc">
                            <div className="oi-name">{i.description}</div>
                            <div className="oi-meta">{i.partNumber} · Qty {i.qty} · ${num(i.unitCost).toFixed(2)}/ea</div>
                          </div>
                          <div className="oi-vendor">{i.vendor}</div>
                          <span className="oi-season">{i.season || "—"}</span>
                          <span className="status-badge" style={{ background: sc.bg + "22", color: sc.text }}>
                            <span className="status-dot" style={{ background: sc.dot }}></span>
                            {i.status}
                          </span>
                          <div className="oi-total">${(i.itemTotal || 0).toFixed(2)}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ══════════ SUMMARY TAB ══════════ */}
          {activeTab === "summary" && (
            <>
              <div className="vendor-summary-grid">
                {Object.keys(vendorTotals).sort((a, b) => vendorTotals[b] - vendorTotals[a]).map((vendor) => {
                  const pct = allItemsTotal > 0 ? (vendorTotals[vendor] / allItemsTotal) * 100 : 0;
                  return (
                    <div key={vendor} className="vendor-summary-card">
                      <div className="vscard-vendor">{vendor}</div>
                      <div className="vscard-amount">${vendorTotals[vendor].toFixed(2)}</div>
                      <div className="vscard-pct">{pct.toFixed(1)}% of total</div>
                      <div className="vscard-bar"><div className="vscard-bar-fill" style={{ width: `${pct}%` }}></div></div>
                    </div>
                  );
                })}
              </div>
              <div className="grand-total-card">
                <div className="gtc-row"><span className="gtc-label">Items Total</span><span className="gtc-value">${allItemsTotal.toFixed(2)}</span></div>
                <div className="gtc-divider"></div>
                <div className="gtc-row"><span className="gtc-label">Total Shipping</span><span className="gtc-value">${totalShipping.toFixed(2)}</span></div>
                <div className="gtc-divider"></div>
                <div className="gtc-row"><span className="gtc-label">Total Tax</span><span className="gtc-value">${totalTax.toFixed(2)}</span></div>
                <div className="gtc-divider"></div>
                <div className="gtc-row"><span className="gtc-label">Total Discounts</span><span className="gtc-value">-${totalDiscount.toFixed(2)}</span></div>
                <div className="gtc-divider"></div>
                <div className="gtc-row"><span className="gtc-label">Grand Total</span><span className="gtc-value gtc-grand">${grandTotal.toFixed(2)}</span></div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}